import React, { useEffect, useMemo, useState } from 'react';
import { Sparkles, Filter, RefreshCw, Link2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../backend/services/supabase';
import { useMetaAlignment } from '../hooks/useMetaAlignment';
import CampaignAssociations from '../components/campaigns/CampaignAssociations';
import { formatCurrency, formatNumber } from '../../shared/utils';
import type { MetaAlignmentAccount, MetaAlignmentCampaign, MetaAlignmentSuggestion } from '../../shared/types/metaAlignment';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'PAUSED', label: 'Paused' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const toInputDate = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const toIsoDateTime = (value?: string) => {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
};

const SuggestionList: React.FC<{
  campaign: MetaAlignmentCampaign;
  suggestions: MetaAlignmentSuggestion[];
  onAccept: (suggestion: MetaAlignmentSuggestion) => Promise<void>;
  isBusy: boolean;
}> = ({ campaign, suggestions, onAccept, isBusy }) => {
  if (!suggestions.length) {
    return (
      <div className="text-sm text-gray-500 dark:text-gray-400">
        No smart suggestions yet. Try adjusting your filters or add associations manually.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {suggestions.map((suggestion) => (
        <div
          key={suggestion.releaseId}
          className="flex flex-col md:flex-row md:items-center justify-between gap-3 rounded-lg border border-blue-200/70 dark:border-blue-800/50 bg-blue-50/70 dark:bg-blue-900/20 px-4 py-3"
        >
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold text-blue-700 dark:text-blue-200">
              <Sparkles className="h-4 w-4" />
              {suggestion.releaseTitle}
              {suggestion.releaseDate && (
                <span className="text-xs font-medium text-blue-500/80 dark:text-blue-300/80">
                  {new Date(suggestion.releaseDate).toLocaleDateString()}
                </span>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {suggestion.artistName ? `Artist: ${suggestion.artistName}` : 'Artist unknown'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 space-y-0.5">
              {suggestion.reasons.map((reason) => (
                <div key={`${suggestion.releaseId}-${reason.label}`}>{reason.label} · +{reason.weight}</div>
              ))}
            </div>
          </div>
          <button
            onClick={() => onAccept(suggestion)}
            disabled={isBusy}
            className="inline-flex items-center gap-2 self-start md:self-auto rounded-md bg-blue-600 text-white px-3 py-2 text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            Link to campaign
          </button>
        </div>
      ))}
    </div>
  );
};

const AssociationBadge: React.FC<{ label: string; value: string; tone?: 'default' | 'warning' }>
  = ({ label, value, tone = 'default' }) => (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium border ${
        tone === 'warning'
          ? 'border-amber-400 text-amber-700 dark:text-amber-200'
          : 'border-emerald-300 text-emerald-700 dark:text-emerald-200'
      } bg-white/80 dark:bg-gray-900/40 shadow-sm`}
    >
      <span className="uppercase tracking-wide text-[10px] text-gray-500 dark:text-gray-400">{label}</span>
      <span>{value}</span>
    </div>
  );

const MetaAlignment: React.FC = () => {
  const { data, filters, loading, error, setFilterValue, resetFilters, refresh } = useMetaAlignment({ limit: 30 });
  const needsReviewOnly = Boolean(filters.needsReview);
  const [accounts, setAccounts] = useState<MetaAlignmentAccount[]>([]);
  const [search, setSearch] = useState(filters.search ?? '');
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);
  const [linkingCampaignId, setLinkingCampaignId] = useState<string | null>(null);

  useEffect(() => {
    const fetchAccounts = async () => {
      const { data: rows, error: accountError } = await supabase
        .from('meta_ad_accounts')
        .select('account_id, account_name')
        .order('account_name', { ascending: true });

      if (accountError) {
        console.error('Failed to load Meta ad accounts:', accountError);
        return;
      }

      const normalized = (rows ?? []).map((row) => ({
        accountId: row.account_id,
        accountName: row.account_name,
      }));
      setAccounts(normalized);
    };

    fetchAccounts();
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilterValue('search', search ? search : undefined);
    }, 350);
    return () => clearTimeout(handle);
  }, [search, setFilterValue]);

  const totals = useMemo(() => {
    if (!data?.campaigns?.length) {
      return { spend: 0, impressions: 0, clicks: 0, ctr: 0 };
    }
    const spend = data.campaigns.reduce((sum, campaign) => sum + (campaign.metrics?.spend || 0), 0);
    const impressions = data.campaigns.reduce((sum, campaign) => sum + (campaign.metrics?.impressions || 0), 0);
    const clicks = data.campaigns.reduce((sum, campaign) => sum + (campaign.metrics?.clicks || 0), 0);
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0;
    return { spend, impressions, clicks, ctr };
  }, [data?.campaigns]);

  const handleAcceptSuggestion = async (campaign: MetaAlignmentCampaign, suggestion: MetaAlignmentSuggestion) => {
    try {
      setLinkingCampaignId(campaign.id);
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      if (!token) {
        toast.error('Unable to link release – missing Supabase session');
        return;
      }

      const response = await fetch('/api/campaigns/associations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          campaign_id: campaign.id,
          campaign_type: 'meta',
          entity_type: 'release',
          entity_id: suggestion.releaseId,
          association_type: 'primary',
          notes: `Suggested match (score ${suggestion.score}) from Meta alignment: ${suggestion.reasons
            .map((reason) => reason.label)
            .join('; ')}`,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Failed to create association');
      }

      toast.success(`Linked ${campaign.name} to ${suggestion.releaseTitle}`);
      await refresh();
    } catch (err) {
      console.error('Failed to accept suggestion:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to link release');
    } finally {
      setLinkingCampaignId(null);
    }
  };

  const renderAssociations = (campaign: MetaAlignmentCampaign) => {
    if (!campaign.associations.length) {
      return (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-300">
          <AlertCircle className="h-4 w-4" /> No associations yet
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {campaign.associations.map((assoc) => (
          <AssociationBadge
            key={`${campaign.id}-${assoc.entityType}-${assoc.entityId}`}
            label={assoc.entityType}
            value={assoc.entityName}
          />
        ))}
      </div>
    );
  };

  const handleDateChange = (key: 'dateFrom' | 'dateTo', value: string) => {
    if (!value) {
      setFilterValue(key, undefined);
      return;
    }
    const iso = toIsoDateTime(value);
    setFilterValue(key, iso);
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-50 flex items-center gap-3">
          Meta Alignment Workspace
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-300 max-w-2xl">
          Match Meta campaigns, ad sets, and ads to releases, artists, and contacts. Accept smart suggestions, confirm
          associations, and keep your marketing activity perfectly aligned with the roster.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Tracked campaigns</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{data?.totalCampaigns ?? 0}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {data?.unmatchedCampaigns ?? 0} need review
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Meta spend in view</div>
          <div className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-50">{formatCurrency(totals.spend)}</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Across current filter set</div>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Performance snapshot</div>
          <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-50">
            {formatNumber(totals.impressions)} impressions · {formatNumber(totals.clicks)} clicks · {totals.ctr.toFixed(2)}% CTR
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Aggregated from campaign-level insights</div>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
            <Filter className="h-4 w-4" /> Filters
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => refresh()}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button
              onClick={() => {
                resetFilters();
                setSearch('');
              }}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Reset
            </button>
          </div>
        </div>
        <div className="px-4 py-4 grid grid-cols-1 lg:grid-cols-5 gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Account</label>
            <select
              value={filters.accountId ?? ''}
              onChange={(event) => setFilterValue('accountId', event.target.value || undefined)}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All accounts</option>
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.accountName}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Status</label>
            <select
              value={filters.status ?? ''}
              onChange={(event) => setFilterValue('status', event.target.value || undefined)}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Updated after</label>
            <input
              type="date"
              value={toInputDate(filters.dateFrom)}
              onChange={(event) => handleDateChange('dateFrom', event.target.value)}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Updated before</label>
            <input
              type="date"
              value={toInputDate(filters.dateTo)}
              onChange={(event) => handleDateChange('dateTo', event.target.value)}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Search campaigns</label>
            <input
              type="text"
              placeholder="Name or Meta ID"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center gap-2 pt-6">
            <input
              id="needs-review-toggle"
              type="checkbox"
              checked={needsReviewOnly}
              onChange={(event) => setFilterValue('needsReview', event.target.checked ? true : undefined)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label
              htmlFor="needs-review-toggle"
              className="text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              Needs review only
            </label>
          </div>
        </div>
      </div>

      {loading && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-gray-600 dark:text-gray-300">
          Loading Meta alignment data…
        </div>
      )}

      {error && !loading && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 text-rose-700 p-4">
          {error}
        </div>
      )}

      {!loading && !error && (!data?.campaigns?.length) && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 text-center text-gray-600 dark:text-gray-300">
          No Meta campaigns match these filters yet.
        </div>
      )}

      <div className="space-y-6">
        {data?.campaigns.map((campaign) => {
          const hasAssociations = campaign.associations.length > 0;
          const isExpanded = expandedCampaignId === campaign.id;

          return (
            <div
              key={campaign.id}
              className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden"
            >
              <div className="px-5 py-5 border-b border-gray-200 dark:border-gray-800">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">{campaign.name}</h2>
                      <span className="text-xs font-mono text-gray-500 dark:text-gray-400">{campaign.externalId}</span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-3">
                      <span className="uppercase tracking-wide text-xs font-semibold text-blue-600 dark:text-blue-300">{campaign.status}</span>
                      <span>Account: {campaign.accountId}</span>
                      {campaign.startTime && (
                        <span>
                          Live since {new Date(campaign.startTime).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="mt-3">
                      {hasAssociations ? (
                        renderAssociations(campaign)
                      ) : (
                        <AssociationBadge label="Needs link" value="No release connected" tone="warning" />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Spend</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{formatCurrency(campaign.metrics.spend)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Impressions</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{formatNumber(campaign.metrics.impressions)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Clicks</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{formatNumber(campaign.metrics.clicks)}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">CTR</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-50">{campaign.metrics.ctr.toFixed(2)}%</div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    onClick={() => setExpandedCampaignId(isExpanded ? null : campaign.id)}
                    className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {isExpanded ? 'Hide ad sets & associations' : 'Review ad sets & manage associations'}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div className="px-5 py-5 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Smart suggestions</h3>
                    <SuggestionList
                      campaign={campaign}
                      suggestions={campaign.suggestions}
                      isBusy={linkingCampaignId === campaign.id}
                      onAccept={(suggestion) => handleAcceptSuggestion(campaign, suggestion)}
                    />
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Manage associations</h3>
                    <CampaignAssociations
                      campaignId={campaign.id}
                      campaignType="meta"
                      campaignName={campaign.name}
                      onAssociationsChange={() => refresh()}
                    />
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wide mb-2">Ad sets & ads</h3>
                    {campaign.adSets.length === 0 ? (
                      <div className="text-sm text-gray-500 dark:text-gray-400">No Meta ad sets were found for this campaign yet.</div>
                    ) : (
                      <div className="space-y-4">
                        {campaign.adSets.map((adSet) => (
                          <div key={adSet.id} className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-900/60 p-4">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                  {adSet.name}
                                  <span className="text-xs uppercase tracking-wide text-blue-600 dark:text-blue-300 font-semibold">{adSet.status}</span>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Ad set ID: {adSet.adsetId}</div>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                                <div>
                                  <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">Spend</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-50">{formatCurrency(adSet.metrics.spend)}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">Impr.</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-50">{formatNumber(adSet.metrics.impressions)}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">Clicks</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-50">{formatNumber(adSet.metrics.clicks)}</div>
                                </div>
                                <div>
                                  <div className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400">CTR</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-50">{adSet.metrics.ctr.toFixed(2)}%</div>
                                </div>
                              </div>
                            </div>

                            {adSet.ads.length > 0 && (
                              <div className="mt-3 border-t border-gray-200 dark:border-gray-800 pt-3 space-y-2">
                                {adSet.ads.map((ad) => (
                                  <div key={ad.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-sm">
                                    <div>
                                      <div className="font-medium text-gray-800 dark:text-gray-200">{ad.name}</div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400">Ad ID: {ad.adId}</div>
                                    </div>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 text-xs">
                                      <div>
                                        <div className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Spend</div>
                                        <div className="font-medium text-gray-800 dark:text-gray-100">{formatCurrency(ad.metrics.spend)}</div>
                                      </div>
                                      <div>
                                        <div className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Impr.</div>
                                        <div className="font-medium text-gray-800 dark:text-gray-100">{formatNumber(ad.metrics.impressions)}</div>
                                      </div>
                                      <div>
                                        <div className="text-[10px] uppercase text-gray-500 dark:text-gray-400">Clicks</div>
                                        <div className="font-medium text-gray-800 dark:text-gray-100">{formatNumber(ad.metrics.clicks)}</div>
                                      </div>
                                      <div>
                                        <div className="text-[10px] uppercase text-gray-500 dark:text-gray-400">CTR</div>
                                        <div className="font-medium text-gray-800 dark:text-gray-100">{ad.metrics.ctr.toFixed(2)}%</div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MetaAlignment;
